import { getAuthSession } from "@/shared/auth/session";
import {
  ANONYMOUS_STORAGE_SCOPE,
  accountStorageScopeForStableId as deriveAccountStorageScopeForStableId,
  acknowledgeAccountStorageBootstrapPendingPush,
  captureAnonymousAccountStorageMigration as captureAnonymousStorageMigration,
  completeAccountStorageBootstrap,
  ensurePendingAccountStorageBootstrap,
  hasPendingAccountStorageBootstrap,
  hasRecoverableAnonymousAccountStorageMigration,
  hasServerCompletedAccountStorageBootstrap,
  markAccountStorageBootstrapPushPending,
  markAccountStorageBootstrapServerCompleted,
  persistAccountStorageBootstrapRequest,
  readAccountStorageBootstrapPendingPushOptions,
  readAccountStorageBootstrapPendingPushState,
  readAccountStorageBootstrapRequest,
  stageAnonymousAccountStorageMigration as stageAnonymousStorageMigration,
  type AccountStorageBootstrapPushOptions,
  type AccountStorageBootstrapPendingPushReceipt,
  type AccountStorageBootstrapRequest,
  type AccountStorageMigrationTransaction,
  type AnonymousAccountStorageMigrationIntent,
} from "./account-storage-migration.mjs";

export { ANONYMOUS_STORAGE_SCOPE };
export type {
  AccountStorageMigrationTransaction,
  AnonymousAccountStorageMigrationIntent,
} from "./account-storage-migration.mjs";

function normalizeScopePart(value: string): string {
  return value
    .trim()
    .replace(/[^a-zA-Z0-9._-]/g, "_")
    .slice(0, 96);
}

export function currentAccountStorageScope(): string {
  const session = getAuthSession();
  return (
    accountStorageScopeForUser(session?.user) ||
    accountStorageScopeForStableId(session?.sessionId) ||
    ANONYMOUS_STORAGE_SCOPE
  );
}

export function accountStorageScopeForStableId(value: string | null | undefined): string | null {
  return deriveAccountStorageScopeForStableId(value);
}

export function accountStorageScopeForUser(
  user: { userId?: string; id?: string } | null | undefined,
): string | null {
  return accountStorageScopeForStableId(user?.userId || user?.id);
}

export function accountScopedStorageKey(baseKey: string): string {
  return `${baseKey}::${currentAccountStorageScope()}`;
}

export function readAccountScopedStorage(baseKey: string): string | null {
  try {
    const primaryKey = accountScopedStorageKey(baseKey);
    const primary = localStorage.getItem(primaryKey);
    if (primary != null) return primary;

    // Before userId was modeled in AuthUser, public-web accidentally scoped
    // account data to the rotating session id. Migrate the current session's
    // row lazily so this fix does not make existing local data disappear.
    const sessionId = normalizeScopePart(getAuthSession()?.sessionId || "");
    const legacyKey = sessionId ? `${baseKey}::account_${sessionId}` : "";
    if (!legacyKey || legacyKey === primaryKey) return null;
    const legacy = localStorage.getItem(legacyKey);
    if (legacy != null) localStorage.setItem(primaryKey, legacy);
    return legacy;
  } catch {
    return null;
  }
}

export function writeAccountScopedStorage(baseKey: string, value: string): void {
  localStorage.setItem(accountScopedStorageKey(baseKey), value);
}

export function listAccountScopedStorage(
  baseKeyPrefix: string,
): Array<{ baseKey: string; value: string }> {
  const suffix = `::${currentAccountStorageScope()}`;
  const rows: Array<{ baseKey: string; value: string }> = [];
  try {
    for (let index = 0; index < localStorage.length; index += 1) {
      const key = localStorage.key(index);
      if (!key || !key.startsWith(baseKeyPrefix) || !key.endsWith(suffix)) continue;
      const value = localStorage.getItem(key);
      if (value == null) continue;
      rows.push({ baseKey: key.slice(0, -suffix.length), value });
    }
  } catch {
    return [];
  }
  return rows;
}

export function isCurrentAccountScopedStorageKey(
  physicalKey: string | null,
  baseKeyPrefix: string,
): boolean {
  const key = String(physicalKey || "");
  return (
    key.startsWith(baseKeyPrefix) &&
    key.endsWith(`::${currentAccountStorageScope()}`)
  );
}

export function removeAccountScopedStorage(baseKey: string): void {
  const primaryKey = accountScopedStorageKey(baseKey);
  localStorage.removeItem(primaryKey);
  const sessionId = normalizeScopePart(getAuthSession()?.sessionId || "");
  const legacyKey = sessionId ? `${baseKey}::account_${sessionId}` : "";
  if (legacyKey && legacyKey !== primaryKey) localStorage.removeItem(legacyKey);
}

/** Delete a captured owner's local output after a successful account deletion.
 * This never resolves the current owner or reads another account's contents. */
export function removeAccountScopedStorageForScope(baseKey: string, scope: string): void {
  if (!baseKey || !/^(?:anon|account_[a-zA-Z0-9._-]{1,96})$/.test(scope)) return;
  localStorage.removeItem(`${baseKey}::${scope}`);
}

function browserLocalStorage(): Storage | null {
  if (typeof window === "undefined") return null;
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

export function captureCurrentAnonymousAccountStorageMigration(): AnonymousAccountStorageMigrationIntent | null {
  return captureAnonymousStorageMigration(browserLocalStorage(), currentAccountStorageScope());
}

export function stageCurrentAnonymousStorageForNewAccount(
  intent: AnonymousAccountStorageMigrationIntent | null,
  user: { userId?: string; id?: string } | null | undefined,
): AccountStorageMigrationTransaction {
  const targetScope = accountStorageScopeForUser(user);
  return stageAnonymousStorageMigration(browserLocalStorage(), intent, targetScope || "", {
    // The target is derived from the authenticated stable user id. Keeping
    // any existing target row makes mixed-version recovery non-destructive;
    // missing anonymous rows remain rollback-capable until auth commits.
    preserveExistingTarget: true,
  });
}

export function hasRecoverableAnonymousStorageForAccount(
  user: { userId?: string; id?: string } | null | undefined,
): boolean {
  const targetScope = accountStorageScopeForUser(user);
  return hasRecoverableAnonymousAccountStorageMigration(browserLocalStorage(), targetScope || "");
}

export function hasCurrentAccountStorageBootstrapPending(): boolean {
  return hasPendingAccountStorageBootstrap(browserLocalStorage(), currentAccountStorageScope());
}

export function hasCurrentAccountStorageBootstrapServerCompleted(): boolean {
  return hasServerCompletedAccountStorageBootstrap(
    browserLocalStorage(),
    currentAccountStorageScope(),
  );
}

export function readCurrentAccountStorageBootstrapRequest(): AccountStorageBootstrapRequest | null {
  return readAccountStorageBootstrapRequest(browserLocalStorage(), currentAccountStorageScope());
}

export function persistCurrentAccountStorageBootstrapRequest(
  request: AccountStorageBootstrapRequest,
): AccountStorageBootstrapRequest | null {
  return persistAccountStorageBootstrapRequest(
    browserLocalStorage(),
    currentAccountStorageScope(),
    request,
  );
}

export function markCurrentAccountStorageBootstrapPushPending(
  options: AccountStorageBootstrapPushOptions,
): boolean {
  return markAccountStorageBootstrapPushPending(
    browserLocalStorage(),
    currentAccountStorageScope(),
    options,
  );
}

export function readCurrentAccountStorageBootstrapPendingPushOptions(): AccountStorageBootstrapPushOptions {
  return readAccountStorageBootstrapPendingPushOptions(
    browserLocalStorage(),
    currentAccountStorageScope(),
  );
}

export function readCurrentAccountStorageBootstrapPendingPushState(): {
  generation: AccountStorageBootstrapPendingPushReceipt;
  options: AccountStorageBootstrapPushOptions;
} {
  return readAccountStorageBootstrapPendingPushState(
    browserLocalStorage(),
    currentAccountStorageScope(),
  );
}

export function acknowledgeCurrentAccountStorageBootstrapPendingPush(
  generation: AccountStorageBootstrapPendingPushReceipt,
): boolean {
  return acknowledgeAccountStorageBootstrapPendingPush(
    browserLocalStorage(),
    currentAccountStorageScope(),
    generation,
  );
}

export function markCurrentAccountStorageBootstrapServerCompleted(): boolean {
  return markAccountStorageBootstrapServerCompleted(
    browserLocalStorage(),
    currentAccountStorageScope(),
  );
}

export function ensureCurrentAccountStorageBootstrapPending(): boolean {
  const session = getAuthSession();
  const targetScope = accountStorageScopeForUser(session?.user);
  return ensurePendingAccountStorageBootstrap(browserLocalStorage(), targetScope || "");
}

export function completeCurrentAccountStorageBootstrap(): boolean {
  return completeAccountStorageBootstrap(browserLocalStorage(), currentAccountStorageScope());
}
